<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      persistent
      max-width="500"
      scrollable
    >
      <v-card>
        <v-card-title class="text-h5" style="background-color: #ef4444; color: white;">
          <v-icon color="white" class="ml-2">mdi-alert-circle</v-icon>
          <span>تأكيد رفض الطلب</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="handleCancel"
            :disabled="loading || success"
            color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Request Info -->
          <div v-if="requestInfo" class="request-info mb-4">
            <div class="info-item mb-2">
              <span class="info-label">رقم الجوال:</span>
              <span class="info-value">{{  requestInfo.customer.mobile }}</span>
            </div>
            <div class="info-item" v-if="requestInfo.customer_name || (requestInfo.customer && requestInfo.customer.name)">
              <span class="info-label">اسم العميل:</span>
              <span class="info-value">{{ requestInfo.customer_name || (requestInfo.customer && requestInfo.customer.name) || 'غير معروف' }}</span>
            </div>
          </div>

          <!-- Confirmation Message -->
          <div class="confirmation-message mb-4">
            <p class="message-text mb-2">
              هل أنت متأكد من رفض هذا الطلب وإرجاعه لخدمة العملاء؟
            </p>
            <p class="message-subtext">
              سيتم إرجاع الطلب إلى موظف خدمة العملاء المعين عليه
            </p>
          </div>

          <!-- Reason Field -->
          <div class="reason-field">
            <v-textarea
              v-model="rejectionReason"
              label="سبب الرفض (اجباري)"
              placeholder="أدخل سبب رفض الطلب (اجباري)"
              rows="4"
              maxlength="1000"
              counter
              outlined
              dense
              :disabled="loading || success"
              :rules="reasonRules"
            ></v-textarea>
          </div>

          <!-- Error Message -->
          <v-alert
            v-if="error"
            type="error"
            dense
            class="mt-3"
            dismissible
            @input="error = null"
          >
            {{ error }}
          </v-alert>

          <!-- Success Message -->
          <v-alert
            v-if="success"
            type="success"
            dense
            class="mt-3"
          >
            تم رفض الطلب وإرجاعه لخدمة العملاء بنجاح
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="handleCancel"
            :disabled="loading || success"
          >
            إلغاء
          </v-btn>
          <v-btn
            color="red darken-1"
            @click="handleConfirm"
            :loading="loading"
            :disabled="success"
          >
            <v-icon left>mdi-check</v-icon>
            تأكيد الرفض
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import Axios from "axios";
import { base } from "@/mixins/base";

export default {
  name: 'AgentRejectRequestDialog',
  mixins: [base],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    reqId: {
      type: [Number, String],
      required: true
    },
    requestInfo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      success: false,
      rejectionReason: '',
      reasonRules: [
        v => !v || v.length <= 1000 || 'يجب ألا يتجاوز السبب 1000 حرف'
      ]
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.resetState();
      }
    }
  },
  methods: {
    async handleConfirm() {
      this.loading = true;
      this.error = null;
      this.success = false;
      if(this.rejectionReason.trim() === ''){
        this.error = "يجب أن يكون لديك سبب رفض الطلب (اجباري)";
        this.loading = false;
        return;
      }
      try {
        const response = await Axios.post(
          `${this.$store.state.url}/v2/employee/Requests/rejectRequestFromAgent/${this.reqId}`,
          {
            reason: this.rejectionReason.trim() || null
          },
          {
            headers: this.request_headers()
          }
        );

        if (response.data && (response.data.success || response.data.success === undefined)) {
          this.success = true;
          
          // Emit success event
          this.$emit('confirmed', {
            requestId: this.reqId,
            response: response.data
          });
          
          // Show success message
          this.showPopup("success", response.data.message || "تم رفض الطلب وإرجاعه لخدمة العملاء بنجاح");
          
          // Refresh request details if method exists
          if (this.$parent && typeof this.$parent.get_request_details === 'function') {
            this.$parent.get_request_details();
          }
          
          // Auto close after 2 seconds
          setTimeout(() => {
            this.handleCancel();
          }, 2000);
        } else {
          this.error = response.data.message || 'حدث خطأ أثناء معالجة الطلب';
        }
      } catch (error) {
        console.error('Error rejecting request:', error);
        
        if (error.response) {
          // Server responded with error
          const errorData = error.response.data;
          if (errorData.message) {
            this.error = Array.isArray(errorData.message) 
              ? errorData.message.join(', ') 
              : errorData.message;
          } else if (errorData.payload) {
            this.error = errorData.payload;
          } else {
            this.error = 'حدث خطأ أثناء معالجة الطلب';
          }
        } else if (error.request) {
          // Request made but no response
          this.error = 'لا يمكن الاتصال بالخادم. يرجى التحقق من الاتصال بالإنترنت';
        } else {
          // Error setting up request
          this.error = 'حدث خطأ غير متوقع';
        }
      } finally {
        this.loading = false;
      }
    },
    handleCancel() {
      if (!this.loading && !this.success) {
        this.$emit('canceled');
        this.resetState();
        this.dialog = false;
      } else if (this.success) {
        this.dialog = false;
      }
    },
    resetState() {
      this.loading = false;
      this.error = null;
      this.success = false;
      this.rejectionReason = '';
    }
  }
}
</script>

<style scoped>
.request-info {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  border-right: 3px solid #3b82f6;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  font-weight: 600;
  color: #6b7280;
  font-size: 14px;
}

.info-value {
  font-weight: 500;
  color: #1f2937;
  font-size: 14px;
}

.confirmation-message {
  margin-bottom: 16px;
}

.message-text {
  font-size: 16px;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.6;
}

.message-subtext {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.reason-field {
  margin-top: 16px;
}
</style>
